import { useRef, useState } from 'react'

import { TGetItemFn, TSetItemFn } from '@/config/types'
import { CheckoutFlowVersion } from '@/enums/payments'

import { ApiService } from '@/api'
import { isContactAccessState, TContactAccessState } from '@/api/types'
import { useAuth } from '@/auth/useAuth'

const contactStateKey = 'contactState'
const contactStateLastCheckKey = 'contactStateLastCheck'
const checkTimeout = 300000 // 5 min

export type TContactState = TContactAccessState | undefined

export type TContactStateWithProgress = [boolean, TContactState]

function isExpired(key: string, timeout: number, getItem: TGetItemFn): boolean {
  const ts = getItem(key)
  if (!ts || isNaN(+ts)) {
    return true
  }

  const timeDiff = new Date().getTime() - +ts

  return timeDiff > timeout
}

export function resetContactStateCheckTimeout(setItem: TSetItemFn): void {
  setItem(contactStateLastCheckKey, undefined)
}

function isStateExpired(getItem: TGetItemFn): boolean {
  return isExpired(contactStateLastCheckKey, checkTimeout, getItem)
}

function saveTimeOfAttemptToLocalStorage(setItem: TSetItemFn): void {
  setItem(contactStateLastCheckKey, new Date().getTime())
}

function saveContactStateToLocalStorage(state: TContactState, setItem: TSetItemFn): void {
  setItem(contactStateKey, state)
  saveTimeOfAttemptToLocalStorage(setItem)
}

function getContactStateFromLocalStorage(getItem: TGetItemFn): TContactState {
  if (isStateExpired(getItem)) {
    return
  }

  const stateFromStorage = getItem(contactStateKey)
  if (stateFromStorage && isContactAccessState(stateFromStorage)) {
    return stateFromStorage
  }
}

function useContactState(
  pageSetId: string,
  getItem: TGetItemFn,
  setItem: TSetItemFn,
  checkoutFlowVersion: CheckoutFlowVersion
): TContactStateWithProgress {
  const { user } = useAuth()
  const [inProgress, setInProgress] = useState<boolean>(false)
  const [contactState, setContactState] = useState<TContactState>()
  const terminalState = useRef<TContactState>()

  if (checkoutFlowVersion === CheckoutFlowVersion.V2 && !user) {
    return [false, terminalState.current]
  }

  if (terminalState.current) {
    return [false, terminalState.current]
  }

  const stateFromStorage = getContactStateFromLocalStorage(getItem)
  if (stateFromStorage) {
    terminalState.current = stateFromStorage
    setContactState(stateFromStorage)
    setInProgress(false)
    return [false, stateFromStorage]
  }

  if (!isStateExpired(getItem)) {
    return [false, contactState]
  }

  if (!inProgress) {
    setInProgress(true)
    ApiService.checkContactState(pageSetId)
      .then(
        (response) => {
          const state = response.data.state
          if (state) {
            terminalState.current = state
          }
          saveContactStateToLocalStorage(state, setItem)
          setContactState(state)
        },
        () => {
          saveTimeOfAttemptToLocalStorage(setItem)
        }
      )
      .finally(() => setInProgress(false))
  }

  return [true, contactState]
}

export default useContactState
